import moment from 'moment';
import React from 'react';

import { DismissAnimation, TransitionGroup } from './dismiss-animation';
import { SectionHeader } from './section-header';
import { TeamCardDueScrollNavigation } from './team-card-due-scroll-navigation';

import styles from './team-cards-due-section.less';

const t = require('views/internal/recup-with-helpers')('home');

interface TeamCardsDueList {
  listName: string;
  groupName: DueDateGroup;
  /* tslint:disable:no-any */
  dueDateCards: any[]; // TODO: TMON-817 Only pass ids down instead of models
  renderDueDate: (dueDateCard: any) => JSX.Element;
  /* tslint:enable:no-any */
  setGroupRef: (groupName: DueDateGroup, groupRef: HTMLDivElement) => void;
}

const TeamCardsDueList: React.StatelessComponent<TeamCardsDueList> = ({
  dueDateCards,
  groupName,
  listName,
  renderDueDate,
  setGroupRef,
}) => (
  <div ref={setGroupRef.bind(null, groupName)}>
    <SectionHeader>{listName}</SectionHeader>
    <TransitionGroup component="ul" className={styles.dueList}>
      {dueDateCards.map((dueDateCard) => (
        <DismissAnimation id={dueDateCard.id} key={dueDateCard.id}>
          <li key={dueDateCard.id} className={styles.dueDateCard}>
            {renderDueDate(dueDateCard)}
          </li>
        </DismissAnimation>
      ))}
    </TransitionGroup>
  </div>
);

type DueDateGroup =
  | 'overdue'
  | 'due-today'
  | 'due-tomorrow'
  | 'due-week'
  | 'due-later';

const getGroup = (now: number, due: number): DueDateGroup => {
  const withinNextDays = (days: number) =>
    moment(now)
      .add(days, 'days')
      .endOf('day')
      .isAfter(due);

  switch (true) {
    case moment(now).isAfter(due):
      return 'overdue';
    case withinNextDays(0):
      return 'due-today';
    case withinNextDays(1):
      return 'due-tomorrow';
    case withinNextDays(7):
      return 'due-week';
    default:
      return 'due-later';
  }
};

const inGroup = (now: number, due: number, group: DueDateGroup) =>
  group === getGroup(now, due);

interface TeamCardsDueSectionProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /* tslint:disable:no-any */
  dueDateCards: any[]; // TODO: TMON-817 Only pass ids down instead of models
  getDueDateOf: (dueDateCard: any) => number;
  onClickGroup: React.EventHandler<React.MouseEvent<HTMLElement>>;
  renderDueDate: (dueDateCard: any) => JSX.Element;
  /* tslint:enable:no-any */
  selectedGroup: DueDateGroup;
  setGroupRef: (groupName: DueDateGroup, groupRef: HTMLDivElement) => void;
  StickyBoxWaitForMount: typeof React.Component;
  now: number;
}

export const TeamCardsDueSection: React.StatelessComponent<
  TeamCardsDueSectionProps
> = ({
  children,
  dueDateCards,
  renderDueDate,
  getDueDateOf,
  now,
  onClickGroup,
  selectedGroup,
  setGroupRef,
  StickyBoxWaitForMount,
  ...props
}) => {
  const groups: {
    group: DueDateGroup;
    name: string;
    /* tslint:disable:no-any */
    cards: any;
    /* tslint:enable:no-any */
  }[] = [
    {
      group: 'overdue',
      name: t.l('overdue'),
      cards: dueDateCards.filter((dueDateCard) =>
        inGroup(now, getDueDateOf(dueDateCard), 'overdue'),
      ),
    },
    {
      group: 'due-today',
      name: t.l('due-today'),
      cards: dueDateCards.filter((dueDateCard) =>
        inGroup(now, getDueDateOf(dueDateCard), 'due-today'),
      ),
    },
    {
      group: 'due-tomorrow',
      name: t.l('due-tomorrow'),
      cards: dueDateCards.filter((dueDateCard) =>
        inGroup(now, getDueDateOf(dueDateCard), 'due-tomorrow'),
      ),
    },
    {
      group: 'due-week',
      name: t.l('due-within-7-days'),
      cards: dueDateCards.filter((dueDateCard) =>
        inGroup(now, getDueDateOf(dueDateCard), 'due-week'),
      ),
    },
    {
      group: 'due-later',
      name: t.l('due-later'),
      cards: dueDateCards.filter((dueDateCard) =>
        inGroup(now, getDueDateOf(dueDateCard), 'due-later'),
      ),
    },
  ];

  return (
    <div className={styles.dueDateSection} {...props}>
      <div>
        {groups.map(({ group, name, cards }) => (
          <TeamCardsDueList
            key={group}
            dueDateCards={cards}
            renderDueDate={renderDueDate}
            listName={name}
            groupName={group}
            setGroupRef={setGroupRef}
          />
        ))}
      </div>

      <StickyBoxWaitForMount className={styles.scrollNavContainer}>
        <TeamCardDueScrollNavigation
          tabs={groups.map(({ group, name, cards }) => ({
            count: cards.length,
            tabText: name,
            selected: selectedGroup === group,
            onClick: onClickGroup.bind(null, group),
          }))}
        />
      </StickyBoxWaitForMount>
      {children}
    </div>
  );
};



// WEBPACK FOOTER //
// ./app/scripts/views/home/presentational/team-cards-due-section.tsx